import type { TFunction } from "i18next"
import type { Locale } from "date-fns"
import { pl as plDateLocale } from "date-fns/locale"
import { useTranslation } from "react-i18next"
import i18n from "./i18n"

// Shared formatting for translated screens. Units like "km" and "%" are the
// same in English and Polish, so they stay in the components.

export const formatMinutesAgo = (t: TFunction, minutesAgo: number): string =>
  minutesAgo >= 60
    ? t("units.hoursAgo", { value: Math.round(minutesAgo / 60) })
    : t("units.minutesAgo", { value: minutesAgo })

export const formatHoursMinutes = (t: TFunction, seconds: number): string =>
  t("units.hoursMinutes", {
    hours: Math.floor(seconds / 3600),
    minutes: Math.floor((seconds % 3600) / 60)
      .toString()
      .padStart(2, "0"),
  })

// Locale for date-fns `format(...)`. `pl` is Polish month and day names.
// `en` and legacy (users who picked no language) are date-fns' English; the
// legacy text is English everywhere, so a call site that used Polish dates
// before now gets English ones too.
export const getDateFnsLocale = (legacy?: Locale): Locale | undefined => {
  if (i18n.language === "pl") return plDateLocale
  if (i18n.language === "en") return undefined
  return legacy === plDateLocale ? undefined : legacy
}

// Locale for Intl and `toLocale*String` calls. In `en`, a call site that
// already used an English locale keeps it; anything else (Polish or the
// browser default) becomes en-GB. Legacy keeps the call site's own locale,
// except a Polish one, which becomes en-GB to match the English text.
export const getIntlLocale = (legacy?: string): string | undefined => {
  if (i18n.language === "pl") return "pl-PL"
  if (i18n.language === "en") return legacy?.startsWith("en") ? legacy : "en-GB"
  return legacy?.startsWith("pl") ? "en-GB" : legacy
}

// Hook versions for components that format dates but show no other text:
// they subscribe to language changes, so the component re-renders on a switch.
export const useDateFnsLocale = (legacy?: Locale): Locale | undefined => {
  useTranslation()
  return getDateFnsLocale(legacy)
}

export const useIntlLocale = (legacy?: string): string | undefined => {
  useTranslation()
  return getIntlLocale(legacy)
}
